import { useState } from "react";
import { motion } from "framer-motion";
import { tap } from "../motion";
import { Select } from "./Select";
import { RangeCalendar } from "./RangeCalendar";
import {
  RESIDENT_STATUSES,
  RESIDENT_STATUS_LABELS,
  residentStatusChipClass,
  type ResidentStatus,
} from "../hooks/useResidents";
import { type ResidentDetail } from "../hooks/useIcpFlow";
import { useStatusHistory, type StatusHistoryBody, type StatusHistoryEntry } from "../hooks/useStatusHistory";

// Status-period timeline (quick view): a manual backfill log with add/edit/delete.
// The resident's current status is shown as a chip in the quick-view header, so
// this panel only manages the history records, never residents.status.

// History records only meaningful, non-default periods: "active" is the
// default status (shown as the current-status chip in the quick-view header)
// and "new" is the admission state (no defined duration), so neither is
// recorded here — the API rejects them too.
const HISTORY_STATUSES = RESIDENT_STATUSES.filter((s) => s !== "active" && s !== "new");
const STATUS_OPTIONS = HISTORY_STATUSES.map((s) => ({ value: s, label: RESIDENT_STATUS_LABELS[s] }));
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Colour used to paint the range bar on the calendar — mirrors the status chip
// semantics (new/hospital/discharged have a semantic colour; active is
// the neutral default).
const STATUS_RANGE_COLOR: Record<ResidentStatus, string> = {
  new: "var(--accent)",
  active: "var(--text-muted)",
  hospital: "var(--warn)",
  discharged: "var(--danger)",
};

function statusLabel(status: string): string {
  return RESIDENT_STATUS_LABELS[status as ResidentStatus] ?? status;
}

function HistoryForm({
  residentId,
  entry,
  defaultStatus,
  anchorEl,
  onDone,
}: {
  residentId: string;
  entry: StatusHistoryEntry | null;
  /** Pre-selected status for a new record (the resident's current status). */
  defaultStatus: string;
  /** The button that opened the form — the calendar popover anchors to it. */
  anchorEl: HTMLElement | null;
  onDone: () => void;
}) {
  const { add, update } = useStatusHistory(residentId);
  const [status, setStatus] = useState(entry?.status ?? (defaultStatus === "active" ? "" : defaultStatus));
  const [startedAt, setStartedAt] = useState(entry?.startedAt ?? "");
  const [endedAt, setEndedAt] = useState(entry?.endedAt ?? "");
  const busy = add.isPending || update.isPending;
  // Status may be a preset option or a custom label typed into the creatable
  // dropdown; "active"/"new" stay forbidden (default / admission state — not
  // recordable periods, the API rejects them too).
  const valid =
    status.trim() !== "" &&
    status !== "active" &&
    status !== "new" &&
    DATE_RE.test(startedAt) &&
    (!endedAt || (DATE_RE.test(endedAt) && endedAt >= startedAt));
  const rangeColor = STATUS_RANGE_COLOR[status as ResidentStatus] ?? "var(--text-muted)";

  function save() {
    const body: StatusHistoryBody = { status, startedAt, endedAt: endedAt || null };
    if (entry) update.mutate({ entryId: entry.id, body }, { onSuccess: onDone });
    else add.mutate(body, { onSuccess: onDone });
  }

  return (
    <RangeCalendar
      start={startedAt || null}
      end={endedAt || null}
      color={rangeColor}
      singleDay={status === "discharged"}
      anchorEl={anchorEl}
      onClose={onDone}
      header={
        <>
          <span className="history-form-label">Status</span>
          <Select ariaLabel="Record status" value={status} onChange={setStatus} options={STATUS_OPTIONS} creatable />
        </>
      }
      onChange={({ start, end }) => {
        setStartedAt(start ?? "");
        setEndedAt(end ?? "");
      }}
      actions={
        <div className="history-form-actions">
          <div className="history-form-row">
            <button type="button" className="btn btn-secondary" onClick={onDone}>Cancel</button>
            <motion.button type="button" className="btn btn-primary" {...tap} disabled={!valid || busy} onClick={save}>
              {busy ? "Saving…" : entry ? "Save" : "Add"}
            </motion.button>
          </div>
          {add.isError || update.isError ? <p className="error">Could not save the record. Try again.</p> : null}
        </div>
      }
    />
  );
}

function Timeline({ detail }: { detail: ResidentDetail }) {
  const { remove } = useStatusHistory(detail.id);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // The button that opened the form — the popover spawns from it, and clicks on
  // it are ignored by the popup's outside-click handler so the button toggles.
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const entries = [...detail.statusHistory].reverse(); // newest first

  const closeForm = () => {
    setAdding(false);
    setEditingId(null);
  };

  return (
    <section className="status-timeline">
      <div className="status-timeline-head">
        <h3 className="section-title" style={{ margin: 0 }}>Status history</h3>
        <button
          type="button"
          className="btn btn-secondary history-add"
          aria-haspopup="dialog"
          aria-expanded={adding}
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setAdding((a) => !a);
            setEditingId(null);
            setConfirmDeleteId(null);
          }}
        >
          + Add record
        </button>
      </div>
      {adding ? (
        <HistoryForm residentId={detail.id} entry={null} defaultStatus={detail.status} anchorEl={anchorEl} onDone={closeForm} />
      ) : null}
      {editingId ? (
        <HistoryForm
          key={editingId}
          residentId={detail.id}
          entry={entries.find((e) => e.id === editingId) ?? null}
          defaultStatus={detail.status}
          anchorEl={anchorEl}
          onDone={closeForm}
        />
      ) : null}
      {entries.length === 0 ? (
        <p className="muted">No status records yet.</p>
      ) : (
        <div className="history-list">
          {entries.map((h) => (
            <div key={h.id} className="history-row">
              <span className={`chip ${residentStatusChipClass(h.status)}`}>{statusLabel(h.status)}</span>
              <span className="muted history-range">
                {h.endedAt === null ? `${h.startedAt} → now` : h.endedAt === h.startedAt ? h.startedAt : `${h.startedAt} → ${h.endedAt}`}
              </span>
              <div className="history-actions">
                <button
                  type="button"
                  className="btn btn-ghost history-btn"
                  aria-haspopup="dialog"
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setEditingId(h.id);
                    setAdding(false);
                    setConfirmDeleteId(null);
                  }}
                >
                  Edit
                </button>
                {confirmDeleteId === h.id ? (
                  <>
                    <button type="button" className="btn btn-ghost history-btn" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                    <button
                      type="button"
                      className="btn btn-ghost history-btn history-btn-danger"
                      onClick={() => { remove.mutate(h.id); setConfirmDeleteId(null); }}
                    >
                      Confirm
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-ghost history-btn history-btn-danger"
                    onClick={() => setConfirmDeleteId(h.id)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {remove.isError ? <p className="error">Could not delete the record. Try again.</p> : null}
    </section>
  );
}

export function ResidentStatusPanel({ detail }: { detail: ResidentDetail }) {
  return <Timeline detail={detail} />;
}
